import { Box, IconButton, InputAdornment, TableCell, TableRow } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { useAdjustmentLotQuantityCalculation, useGetAdjustmentLotProductTest } from "../../hooks"
import type { LotCorrectionAdjustmentFormValues } from "../../schemas"
import { AdjustmentDuplicateLotError } from "../AdjustmentDuplicateLotError"

import { FormNumericField, FormSelect, FormTextField } from "@/components/UiKit"
import { WarningMessage } from "@/components/WarningMessage/WarningMessage"
import { PRODUCT_PACKAGING_TYPE_OPTIONS } from "@/constants/product"
import { type InventoryPalletGroup, type Product } from "@/graphql/codegen/graphql"
import { getUntestedLotMessage } from "@/utils/lot"
import { isNil } from "@/utils/utils"

interface LotCorrectionAdjustmentNewLotRowProps {
  product: Product
  uomName: string
  index: number
  pallet: Omit<InventoryPalletGroup, "lots"> | null
  isDuplicate: boolean
  onRemove: () => void
}

export const LotCorrectionAdjustmentNewLotRow: FC<LotCorrectionAdjustmentNewLotRowProps> = ({
  product,
  uomName,
  index,
  pallet,
  isDuplicate,
  onRemove,
}) => {
  const { control, watch } = useFormContext<LotCorrectionAdjustmentFormValues>()

  const baseFieldPath = `newLots.${index}` as const
  const fieldPaths = {
    lotNumber: `${baseFieldPath}.lotNumber` as const,
    batchNumber: `${baseFieldPath}.batchNumber` as const,
    quantityPerPackage: `${baseFieldPath}.quantityPerPackage` as const,
    packagingType: `${baseFieldPath}.packagingType` as const,
    unitCount: `${baseFieldPath}.unitCount` as const,
    quantity: `${baseFieldPath}.quantity` as const,
  }

  const [lotNumber, batchNumber] = watch([fieldPaths.lotNumber, fieldPaths.batchNumber])

  const { handleQuantityPerPackageChange, handleUnitCountChange, handleQuantityChange } =
    useAdjustmentLotQuantityCalculation({
      fieldPaths: {
        quantityPerPackage: fieldPaths.quantityPerPackage,
        unitCount: fieldPaths.unitCount,
        quantity: fieldPaths.quantity,
      },
    })

  const { shouldShowUntestedLotWarning, remainingShelfLife } = useGetAdjustmentLotProductTest({
    productId: product.productId,
    lotNumber,
    batchNumber,
  })

  return (
    <TableRow className='align-top' data-testid={`inventory.lot-correction-adjustment-new-lot-row-${index}`}>
      <TableCell className='pt-6'>{pallet?.referenceNbr}</TableCell>
      <TableCell className='pt-6'>{pallet?.location}</TableCell>
      <TableCell>
        <Box className='flex flex-col gap-y-2'>
          <FormTextField
            control={control}
            name={fieldPaths.lotNumber}
            required
            data-testid={`inventory.lot-correction-adjustment-new-lot-row.lot-number-field-${index}`}
          />
          {shouldShowUntestedLotWarning && (
            <WarningMessage
              message={getUntestedLotMessage(batchNumber)}
              data-testid={`inventory.lot-correction-adjustment-new-lot-row.untested-lot-warning-${index}`}
            />
          )}
          {isDuplicate && (
            <AdjustmentDuplicateLotError
              data-testid={`inventory.lot-correction-adjustment-new-lot-row.duplicate-lot-error-${index}`}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <FormTextField
          control={control}
          name={fieldPaths.batchNumber}
          data-testid={`inventory.lot-correction-adjustment-new-lot-row.batch-number-field-${index}`}
        />
      </TableCell>
      <TableCell className='whitespace-nowrap pt-6'>
        {isNil(remainingShelfLife) ? "" : `${remainingShelfLife} %`}
      </TableCell>
      <TableCell>
        <FormSelect
          control={control}
          name={fieldPaths.packagingType}
          required
          placeholder='Select one'
          options={PRODUCT_PACKAGING_TYPE_OPTIONS}
          className='w-32'
          data-testid={`inventory.lot-correction-adjustment-new-lot-row.packaging-field-${index}`}
        />
      </TableCell>
      <TableCell>
        <FormNumericField
          control={control}
          name={fieldPaths.unitCount}
          required
          allowNegative={false}
          decimalScale={0}
          className='w-24'
          onChange={handleUnitCountChange}
          data-testid={`inventory.lot-correction-adjustment-new-lot-row.units-count-field-${index}`}
        />
      </TableCell>
      <TableCell>
        <FormNumericField
          control={control}
          name={fieldPaths.quantityPerPackage}
          required
          allowNegative={false}
          decimalScale={2}
          slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> } }}
          className='w-28'
          onChange={handleQuantityPerPackageChange}
          data-testid={`inventory.lot-correction-adjustment-new-lot-row.package-quantity-field-${index}`}
        />
      </TableCell>
      <TableCell />
      <TableCell>
        <FormNumericField
          control={control}
          name={fieldPaths.quantity}
          required
          allowNegative={false}
          decimalScale={2}
          slotProps={{ input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> } }}
          className='w-40'
          onChange={handleQuantityChange}
          data-testid={`inventory.lot-correction-adjustment-new-lot-row.quantity-field-${index}`}
        />
      </TableCell>
      <TableCell />
      <TableCell className='pt-4'>
        <IconButton
          size='small'
          onClick={onRemove}
          data-testid={`inventory.lot-correction-adjustment-new-lot-row.remove-button-${index}`}
        >
          <DeleteOutlined />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}
